"use strict";
// @typescript:-strictNullChecks
Object.defineProperty(exports, "__esModule", { value: true });
/******************************************************************************
 * Vec2
 *****************************************************************************/
var Vec2 = /** @class */ (function () {
    function Vec2(x, y) {
        if (x === void 0) {
            x = 0.0;
        }
        if (y === void 0) {
            y = 0.0;
        }
        this.x = x;
        this.y = y;
    }
    // Static binary methods. Operate on a and b, store result in c
    Vec2.negative = function (a, b) {
        if (!b) {
            b = new Vec2();
        }
        b.x = -a.x;
        b.y = -a.y;
        return b;
    };
    Vec2.add = function (a, b, c) {
        if (!c) {
            c = new Vec2();
        }
        if (typeof b === 'number') {
            c.x = a.x + b;
            c.y = a.y + b;
        }
        else {
            c.x = a.x + b.x;
            c.y = a.y + b.y;
        }
        return c;
    };
    Vec2.subtract = function (a, b, c) {
        if (!c) {
            c = new Vec2();
        }
        if (typeof b === 'number') {
            c.x = a.x - b;
            c.y = a.y - b;
        }
        else {
            c.x = a.x - b.x;
            c.y = a.y - b.y;
        }
        return c;
    };
    Vec2.multiply = function (a, b, c) {
        if (!c) {
            c = new Vec2();
        }
        if (typeof b === 'number') {
            c.x = a.x * b;
            c.y = a.y * b;
        }
        else {
            c.x = a.x * b.x;
            c.y = a.y * b.y;
        }
        return c;
    };
    Vec2.divide = function (a, b, c) {
        if (!c) {
            c = new Vec2();
        }
        if (typeof b === 'number') {
            c.x = a.x / b;
            c.y = a.y / b;
        }
        else {
            c.x = a.x / b.x;
            c.y = a.y / b.y;
        }
        return c;
    };
    Vec2.lerp = function (a, b, t, r) {
        if (!r) {
            r = new Vec2();
        }
        r.x = a.x + (b.x - a.x) * t;
        r.y = a.y + (b.y - a.y) * t;
        return r;
    };
    Vec2.min = function (a, b, r) {
        if (!r) {
            r = new Vec2();
        }
        r.x = Math.min(a.x, b.x);
        r.y = Math.min(a.y, b.y);
        return r;
    };
    Vec2.max = function (a, b, r) {
        if (!r) {
            r = new Vec2();
        }
        r.x = Math.max(a.x, b.x);
        r.y = Math.max(a.y, b.y);
        return r;
    };
    // Basics
    Vec2.prototype.init = function (x, y) {
        if (x === void 0) {
            x = 0.0;
        }
        if (y === void 0) {
            y = 0.0;
        }
        this.x = x;
        this.y = y;
        return this;
    };
    Vec2.prototype.clone = function () {
        return new Vec2(this.x, this.y);
    };
    Vec2.prototype.toArray = function (n) {
        if (n === void 0) {
            n = 3;
        }
        return [this.x, this.y].slice(0, n);
    };
    Vec2.prototype.copy = function (a) {
        this.x = a.x;
        this.y = a.y;
        return this;
    };
    // Queries
    Vec2.prototype.length = function () {
        return Math.sqrt(this.x * this.x + this.y * this.y);
    };
    Vec2.prototype.min = function () {
        return Math.min(this.x, this.y);
    };
    Vec2.prototype.max = function () {
        return Math.max(this.x, this.y);
    };
    // Mutators
    Vec2.prototype.normalize = function () {
        var l = this.length();
        this.x = this.x / l;
        this.y = this.y / l;
        return this;
    };
    Vec2.prototype.negate = function () {
        this.x = -this.x;
        this.y = -this.y;
        return this;
    };
    // Unary
    Vec2.prototype.dot = function (v) {
        return this.x * v.x + this.y * v.y;
    };
    return Vec2;
}());
exports.Vec2 = Vec2;
/******************************************************************************
 * Vec3
 *****************************************************************************/
var Vec3 = /** @class */ (function () {
    function Vec3(x, y, z) {
        if (x === void 0) {
            x = 0.0;
        }
        if (y === void 0) {
            y = 0.0;
        }
        if (z === void 0) {
            z = 0.0;
        }
        this.x = x;
        this.y = y;
        this.z = z;
    }
    Vec3.fromFloat32Array = function (a, offset, r) {
        if (offset === void 0) {
            offset = 0;
        }
        if (r) {
            return r.init(a[offset + 0], a[offset + 1], a[offset + 2]);
        }
        else {
            return new Vec3(a[offset + 0], a[offset + 1], a[offset + 2]);
        }
    };
    // Static binary methods. Operate on a and b, store result in c
    Vec3.negative = function (a, b) {
        b.x = -a.x;
        b.y = -a.y;
        b.z = -a.z;
        return b;
    };
    Vec3.add = function (a, b, c) {
        if (typeof b === 'number') {
            c.x = a.x + b;
            c.y = a.y + b;
            c.z = a.z + b;
        }
        else {
            c.x = a.x + b.x;
            c.y = a.y + b.y;
            c.z = a.z + b.z;
        }
        return c;
    };
    Vec3.subtract = function (a, b, c) {
        if (typeof b === 'number') {
            c.x = a.x - b;
            c.y = a.y - b;
            c.z = a.z - b;
        }
        else {
            c.x = a.x - b.x;
            c.y = a.y - b.y;
            c.z = a.z - b.z;
        }
        return c;
    };
    Vec3.multiply = function (a, b, c) {
        if (typeof b === 'number') {
            c.x = a.x * b;
            c.y = a.y * b;
            c.z = a.z * b;
        }
        else {
            c.x = a.x * b.x;
            c.y = a.y * b.y;
            c.z = a.z * b.z;
        }
        return c;
    };
    Vec3.divide = function (a, b, c) {
        if (typeof b === 'number') {
            c.x = a.x / b;
            c.y = a.y / b;
            c.z = a.z / b;
        }
        else {
            c.x = a.x / b.x;
            c.y = a.y / b.y;
            c.z = a.z / b.z;
        }
        return c;
    };
    Vec3.dot = function (a, b) {
        return a.x * b.x + a.y * b.y + a.z * b.z;
    };
    Vec3.distance = function (a, b) {
        var x = (b.x - a.x);
        var y = (b.y - a.y);
        var z = (b.z - a.z);
        return Math.sqrt(x * x + y * y + z * z);
    };
    Vec3.distanceSquared = function (a, b) {
        var x = (b.x - a.x);
        var y = (b.y - a.y);
        var z = (b.z - a.z);
        return x * x + y * y + z * z;
    };
    Vec3.cross = function (a, b, c) {
        c.x = a.y * b.z - a.z * b.y;
        c.y = a.z * b.x - a.x * b.z;
        c.z = a.x * b.y - a.y * b.x;
        return c;
    };
    Vec3.lerp = function (a, b, t, r) {
        r.x = a.x + (b.x - a.x) * t;
        r.y = a.y + (b.y - a.y) * t;
        r.z = a.z + (b.z - a.z) * t;
        return r;
    };
    // Basics
    Vec3.prototype.init = function (x, y, z) {
        if (x === void 0) {
            x = 0.0;
        }
        if (y === void 0) {
            y = 0.0;
        }
        if (z === void 0) {
            z = 0.0;
        }
        this.x = x;
        this.y = y;
        this.z = z;
        return this;
    };
    Vec3.prototype.clone = function () {
        return new Vec3(this.x, this.y, this.z);
    };
    Vec3.prototype.toArray = function (n) {
        if (n === void 0) {
            n = 3;
        }
        return [this.x, this.y, this.z].slice(0, n);
    };
    Vec3.prototype.copy = function (a) {
        this.x = a.x;
        this.y = a.y;
        this.z = a.z;
        return this;
    };
    // Queries
    Vec3.prototype.length = function () {
        return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z);
    };
    Vec3.prototype.min = function () {
        return Math.min(Math.min(this.x, this.y), this.z);
    };
    Vec3.prototype.max = function () {
        return Math.max(Math.max(this.x, this.y), this.z);
    };
    // Mutators
    Vec3.prototype.normalize = function () {
        var l = this.length();
        this.x = this.x / l;
        this.y = this.y / l;
        this.z = this.z / l;
        return this;
    };
    Vec3.prototype.negate = function () {
        this.x = -this.x;
        this.y = -this.y;
        this.z = -this.z;
        return this;
    };
    // Unary
    Vec3.prototype.dot = function (v) {
        return this.x * v.x + this.y * v.y + this.z * v.z;
    };
    Vec3.UNIT_X = new Vec3(1, 0, 0);
    Vec3.UNIT_Y = new Vec3(0, 1, 0);
    Vec3.UNIT_Z = new Vec3(0, 0, 1);
    return Vec3;
}());
exports.Vec3 = Vec3;
/******************************************************************************
 * Vec4
 *****************************************************************************/
var Vec4 = /** @class */ (function () {
    function Vec4(x, y, z, w) {
        if (x === void 0) {
            x = 0.0;
        }
        if (y === void 0) {
            y = 0.0;
        }
        if (z === void 0) {
            z = 0.0;
        }
        if (w === void 0) {
            w = 0.0;
        }
        this.x = x;
        this.y = y;
        this.z = z;
        this.w = w;
    }
    Vec4.fromVec3 = function (a, w, r) {
        if (w === void 0) {
            w = 0.0;
        }
        if (r) {
            r.x = a.x;
            r.y = a.y;
            r.z = a.z;
            r.w = w;
            return r;
        }
        else {
            return new Vec4(a.x, a.y, a.z, w);
        }
    };
    Vec4.fromFloat32Array = function (a, offset, r) {
        if (offset === void 0) {
            offset = 0;
        }
        if (r) {
            return r.init(a[offset + 0], a[offset + 1], a[offset + 2], a[offset + 3]);
        }
        else {
            return new Vec4(a[offset + 0], a[offset + 1], a[offset + 2], a[offset + 3]);
        }
    };
    // Static binary methods. Operate on a and b, store result in c
    Vec4.negative = function (a, b) {
        b.x = -a.x;
        b.y = -a.y;
        b.z = -a.z;
        b.w = -a.w;
        return b;
    };
    Vec4.add = function (a, b, c) {
        if (typeof b === 'number') {
            c.x = a.x + b;
            c.y = a.y + b;
            c.z = a.z + b;
            c.w = a.w + b;
        }
        else {
            c.x = a.x + b.x;
            c.y = a.y + b.y;
            c.z = a.z + b.z;
            c.w = a.w * b.w;
        }
        return c;
    };
    Vec4.subtract = function (a, b, c) {
        if (typeof b === 'number') {
            c.x = a.x - b;
            c.y = a.y - b;
            c.z = a.z - b;
            c.w = a.w - b;
        }
        else {
            c.x = a.x - b.x;
            c.y = a.y - b.y;
            c.z = a.z - b.z;
            c.w = a.w - b.w;
        }
        return c;
    };
    Vec4.multiply = function (a, b, c) {
        if (typeof b === 'number') {
            c.x = a.x * b;
            c.y = a.y * b;
            c.z = a.z * b;
            c.w = a.w * b;
        }
        else {
            c.x = a.x * b.x;
            c.y = a.y * b.y;
            c.z = a.z * b.z;
            c.w = a.w * b.w;
        }
        return c;
    };
    Vec4.divide = function (a, b, c) {
        if (typeof b === 'number') {
            c.x = a.x / b;
            c.y = a.y / b;
            c.z = a.z / b;
            c.w = a.w / b;
        }
        else {
            c.x = a.x / b.x;
            c.y = a.y / b.y;
            c.z = a.z / b.z;
            c.w = a.w / b.w;
        }
        return c;
    };
    Vec4.lerp = function (a, b, t, r) {
        r.x = a.x + (b.x - a.x) * t;
        r.y = a.y + (b.y - a.y) * t;
        r.z = a.z + (b.z - a.z) * t;
        r.w = a.w + (b.w - a.w) * t;
        return r;
    };
    Vec4.dot = function (a, b) {
        return a.x * b.x + a.y * b.y + a.z * b.z + a.w * b.w;
    };
    // Basics
    Vec4.prototype.init = function (x, y, z, w) {
        if (x === void 0) {
            x = 0.0;
        }
        if (y === void 0) {
            y = 0.0;
        }
        if (z === void 0) {
            z = 0.0;
        }
        if (w === void 0) {
            w = 0.0;
        }
        this.x = x;
        this.y = y;
        this.z = z;
        this.w = w;
        return this;
    };
    Vec4.prototype.clone = function () {
        return new Vec4(this.x, this.y, this.z, this.w);
    };
    Vec4.prototype.toArray = function (n) {
        if (n === void 0) {
            n = 4;
        }
        return [this.x, this.y, this.z, this.w].slice(0, n);
    };
    Vec4.prototype.copy = function (a) {
        this.x = a.x;
        this.y = a.y;
        this.z = a.z;
        this.w = a.w;
        return this;
    };
    // Queries
    Vec4.prototype.length = function () {
        return Math.sqrt(this.x * this.x + this.y * this.y + this.z * this.z + this.w * this.w);
    };
    Vec4.prototype.min = function () {
        return Math.min(Math.min(Math.min(this.x, this.y), this.z), this.w);
    };
    Vec4.prototype.max = function () {
        return Math.max(Math.max(Math.max(this.x, this.y), this.z), this.w);
    };
    // Mutators
    Vec4.prototype.normalize = function () {
        var l = this.length();
        this.x = this.x / l;
        this.y = this.y / l;
        this.z = this.z / l;
        this.w = this.w / l;
        return this;
    };
    Vec4.prototype.negate = function () {
        this.x = -this.x;
        this.y = -this.y;
        this.z = -this.z;
        this.w = -this.w;
        return this;
    };
    // Unary
    Vec4.prototype.dot = function (v) {
        return this.x * v.x + this.y * v.y + this.z * v.z + this.w * v.w;
    };
    return Vec4;
}());
exports.Vec4 = Vec4;
/******************************************************************************
 * Matrix4
 *****************************************************************************/
var Matrix4 = /** @class */ (function () {
    function Matrix4(m) {
        if (m === void 0) {
            m = kIdentity4;
        }
        this.m = new Float64Array(m);
    }
    Matrix4.prototype.copy = function (a) {
        this.m.set(a.m);
    };
    Matrix4.prototype.clone = function () {
        return new Matrix4(this.m);
    };
    Matrix4.computeView = function (pos, dir, up, right, r) {
        if (r === void 0) {
            r = new Matrix4();
        }
        r.m[0] = right.x;
        r.m[4] = right.y;
        r.m[8] = right.z;
        r.m[12] = -Vec3.dot(right, pos);
        r.m[1] = up.x;
        r.m[5] = up.y;
        r.m[9] = up.z;
        r.m[13] = -Vec3.dot(up, pos);
        r.m[2] = -dir.x;
        r.m[6] = -dir.y;
        r.m[10] = -dir.z;
        r.m[14] = Vec3.dot(dir, pos);
        r.m[3] = 0.0;
        r.m[7] = 0.0;
        r.m[11] = 0.0;
        r.m[15] = 1.0;
        return r;
    };
    Matrix4.fromColumns = function (col0, col1, col2, col3, r) {
        if (r === void 0) {
            r = new Matrix4();
        }
        r.m[0] = col0.x;
        r.m[4] = col1.x;
        r.m[8] = col2.x;
        r.m[12] = col3.x;
        r.m[1] = col0.y;
        r.m[5] = col1.y;
        r.m[9] = col2.y;
        r.m[13] = col3.y;
        r.m[2] = col0.z;
        r.m[6] = col1.z;
        r.m[10] = col2.z;
        r.m[14] = col3.z;
        r.m[3] = col0.w;
        r.m[7] = col1.w;
        r.m[11] = col2.w;
        r.m[15] = col3.w;
        return r;
    };
    Matrix4.fromRotationZ = function (r, result) {
        if (result === void 0) {
            result = new Matrix4();
        }
        var cosR = Math.cos(r);
        var sinR = Math.sin(r);
        var m = result.m;
        m[0] = cosR;
        m[4] = -sinR;
        m[8] = 0;
        m[12] = 0;
        m[1] = sinR;
        m[5] = cosR;
        m[9] = 0;
        m[13] = 0;
        m[2] = 0;
        m[6] = 0;
        m[10] = 1;
        m[14] = 0;
        m[3] = 0;
        m[7] = 0;
        m[11] = 0;
        m[15] = 1;
        return result;
    };
    Matrix4.fromRotationXY = function (r, vec, result) {
        if (result === void 0) {
            result = new Matrix4();
        }
        var u = vec.x;
        var v = vec.y;
        var cosR = Math.cos(r);
        var sinR = Math.sin(r);
        var m = result.m;
        m[0] = u * u + (1 - u * u) * cosR;
        m[4] = u * v * (1 - cosR);
        m[8] = v * sinR;
        m[12] = 0;
        m[1] = u * v * (1 - cosR);
        m[5] = v * v + (1 - v * v) * cosR;
        m[9] = -u * sinR;
        m[13] = 0;
        m[2] = -v * sinR;
        m[6] = u * sinR;
        m[10] = cosR;
        m[14] = 0;
        m[3] = 0;
        m[7] = 0;
        m[11] = 0;
        m[15] = 1;
        return result;
    };
    Matrix4.perspective = function (fovyRadians, aspect, zNear, zFar, result) {
        if (result === void 0) {
            result = new Matrix4();
        }
        var PI_OVER_2 = Math.PI * 0.5;
        var f = Math.tan(PI_OVER_2 - (0.5 * fovyRadians));
        var rangeInv = (1.0 / (zNear - zFar));
        result.init([
            (f / aspect), 0.0, 0.0, 0.0,
            0.0, f, 0.0, 0.0,
            0.0, 0.0, ((zNear + zFar) * rangeInv), -1.0,
            0.0, 0.0, (((zNear * zFar) * rangeInv) * 2.0), 0.0
        ]);
        return result;
    };
    Matrix4.inverse = function (a, result) {
        if (result === void 0) {
            result = new Matrix4();
        }
        // Based on http://www.euclideanspace.com/maths/algebra/matrix/functions/inverse/fourD/index.htm
        var r = result.m, me = a.m, n11 = me[0], n21 = me[1], n31 = me[2], n41 = me[3], n12 = me[4], n22 = me[5], n32 = me[6], n42 = me[7], n13 = me[8], n23 = me[9], n33 = me[10], n43 = me[11], n14 = me[12], n24 = me[13], n34 = me[14], n44 = me[15], t11 = n23 * n34 * n42 - n24 * n33 * n42 + n24 * n32 * n43 - n22 * n34 * n43 - n23 * n32 * n44 + n22 * n33 * n44, t12 = n14 * n33 * n42 - n13 * n34 * n42 - n14 * n32 * n43 + n12 * n34 * n43 + n13 * n32 * n44 - n12 * n33 * n44, t13 = n13 * n24 * n42 - n14 * n23 * n42 + n14 * n22 * n43 - n12 * n24 * n43 - n13 * n22 * n44 + n12 * n23 * n44, t14 = n14 * n23 * n32 - n13 * n24 * n32 - n14 * n22 * n33 + n12 * n24 * n33 + n13 * n22 * n34 - n12 * n23 * n34;
        var det = n11 * t11 + n21 * t12 + n31 * t13 + n41 * t14;
        if (det === 0) {
            throw new Error("Can't invert matrix, determinant is 0");
            return result.init(kIdentity4);
        }
        var detInv = 1 / det;
        r[0] = t11 * detInv;
        r[1] = (n24 * n33 * n41 - n23 * n34 * n41 - n24 * n31 * n43 + n21 * n34 * n43 + n23 * n31 * n44 - n21 * n33 * n44) * detInv;
        r[2] = (n22 * n34 * n41 - n24 * n32 * n41 + n24 * n31 * n42 - n21 * n34 * n42 - n22 * n31 * n44 + n21 * n32 * n44) * detInv;
        r[3] = (n23 * n32 * n41 - n22 * n33 * n41 - n23 * n31 * n42 + n21 * n33 * n42 + n22 * n31 * n43 - n21 * n32 * n43) * detInv;
        r[4] = t12 * detInv;
        r[5] = (n13 * n34 * n41 - n14 * n33 * n41 + n14 * n31 * n43 - n11 * n34 * n43 - n13 * n31 * n44 + n11 * n33 * n44) * detInv;
        r[6] = (n14 * n32 * n41 - n12 * n34 * n41 - n14 * n31 * n42 + n11 * n34 * n42 + n12 * n31 * n44 - n11 * n32 * n44) * detInv;
        r[7] = (n12 * n33 * n41 - n13 * n32 * n41 + n13 * n31 * n42 - n11 * n33 * n42 - n12 * n31 * n43 + n11 * n32 * n43) * detInv;
        r[8] = t13 * detInv;
        r[9] = (n14 * n23 * n41 - n13 * n24 * n41 - n14 * n21 * n43 + n11 * n24 * n43 + n13 * n21 * n44 - n11 * n23 * n44) * detInv;
        r[10] = (n12 * n24 * n41 - n14 * n22 * n41 + n14 * n21 * n42 - n11 * n24 * n42 - n12 * n21 * n44 + n11 * n22 * n44) * detInv;
        r[11] = (n13 * n22 * n41 - n12 * n23 * n41 - n13 * n21 * n42 + n11 * n23 * n42 + n12 * n21 * n43 - n11 * n22 * n43) * detInv;
        r[12] = t14 * detInv;
        r[13] = (n13 * n24 * n31 - n14 * n23 * n31 + n14 * n21 * n33 - n11 * n24 * n33 - n13 * n21 * n34 + n11 * n23 * n34) * detInv;
        r[14] = (n14 * n22 * n31 - n12 * n24 * n31 - n14 * n21 * n32 + n11 * n24 * n32 + n12 * n21 * n34 - n11 * n22 * n34) * detInv;
        r[15] = (n12 * n23 * n31 - n13 * n22 * n31 + n13 * n21 * n32 - n11 * n23 * n32 - n12 * n21 * n33 + n11 * n22 * n33) * detInv;
        return result;
    };
    Matrix4.transpose = function (a, result) {
        if (result === void 0) {
            result = new Matrix4();
        }
        var m = a.m;
        var r = result.m;
        r[0] = m[0];
        r[1] = m[4];
        r[2] = m[8];
        r[3] = m[12];
        r[4] = m[1];
        r[5] = m[5];
        r[6] = m[9];
        r[7] = m[13];
        r[8] = m[2];
        r[9] = m[6];
        r[10] = m[10];
        r[11] = m[14];
        r[12] = m[3];
        r[13] = m[7];
        r[14] = m[11];
        r[15] = m[15];
        return result;
    };
    Matrix4.multiply = function (left, right, result) {
        if (result === void 0) {
            result = new Matrix4();
        }
        var left0 = left.m[0];
        var left1 = left.m[1];
        var left2 = left.m[2];
        var left3 = left.m[3];
        var left4 = left.m[4];
        var left5 = left.m[5];
        var left6 = left.m[6];
        var left7 = left.m[7];
        var left8 = left.m[8];
        var left9 = left.m[9];
        var left10 = left.m[10];
        var left11 = left.m[11];
        var left12 = left.m[12];
        var left13 = left.m[13];
        var left14 = left.m[14];
        var left15 = left.m[15];
        var right0 = right.m[0];
        var right1 = right.m[1];
        var right2 = right.m[2];
        var right3 = right.m[3];
        var right4 = right.m[4];
        var right5 = right.m[5];
        var right6 = right.m[6];
        var right7 = right.m[7];
        var right8 = right.m[8];
        var right9 = right.m[9];
        var right10 = right.m[10];
        var right11 = right.m[11];
        var right12 = right.m[12];
        var right13 = right.m[13];
        var right14 = right.m[14];
        var right15 = right.m[15];
        var column0Row0 = left0 * right0 + left4 * right1 + left8 * right2 + left12 * right3;
        var column0Row1 = left1 * right0 + left5 * right1 + left9 * right2 + left13 * right3;
        var column0Row2 = left2 * right0 + left6 * right1 + left10 * right2 + left14 * right3;
        var column0Row3 = left3 * right0 + left7 * right1 + left11 * right2 + left15 * right3;
        var column1Row0 = left0 * right4 + left4 * right5 + left8 * right6 + left12 * right7;
        var column1Row1 = left1 * right4 + left5 * right5 + left9 * right6 + left13 * right7;
        var column1Row2 = left2 * right4 + left6 * right5 + left10 * right6 + left14 * right7;
        var column1Row3 = left3 * right4 + left7 * right5 + left11 * right6 + left15 * right7;
        var column2Row0 = left0 * right8 + left4 * right9 + left8 * right10 + left12 * right11;
        var column2Row1 = left1 * right8 + left5 * right9 + left9 * right10 + left13 * right11;
        var column2Row2 = left2 * right8 + left6 * right9 + left10 * right10 + left14 * right11;
        var column2Row3 = left3 * right8 + left7 * right9 + left11 * right10 + left15 * right11;
        var column3Row0 = left0 * right12 + left4 * right13 + left8 * right14 + left12 * right15;
        var column3Row1 = left1 * right12 + left5 * right13 + left9 * right14 + left13 * right15;
        var column3Row2 = left2 * right12 + left6 * right13 + left10 * right14 + left14 * right15;
        var column3Row3 = left3 * right12 + left7 * right13 + left11 * right14 + left15 * right15;
        result.m[0] = column0Row0;
        result.m[1] = column0Row1;
        result.m[2] = column0Row2;
        result.m[3] = column0Row3;
        result.m[4] = column1Row0;
        result.m[5] = column1Row1;
        result.m[6] = column1Row2;
        result.m[7] = column1Row3;
        result.m[8] = column2Row0;
        result.m[9] = column2Row1;
        result.m[10] = column2Row2;
        result.m[11] = column2Row3;
        result.m[12] = column3Row0;
        result.m[13] = column3Row1;
        result.m[14] = column3Row2;
        result.m[15] = column3Row3;
        return result;
    };
    Matrix4.prototype.setColumn = function (idx, a) {
        this.m[4 * idx + 0] = a.x;
        this.m[4 * idx + 1] = a.y;
        this.m[4 * idx + 2] = a.z;
        this.m[4 * idx + 3] = a.w;
    };
    Matrix4.prototype.getColumn = function (idx, result) {
        if (result === void 0) {
            result = new Vec4();
        }
        result.x = this.m[4 * idx + 0];
        result.y = this.m[4 * idx + 1];
        result.z = this.m[4 * idx + 2];
        result.w = this.m[4 * idx + 3];
        return result;
    };
    Matrix4.prototype.getColumnXYZ = function (idx, result) {
        if (result === void 0) {
            result = new Vec3();
        }
        result.x = this.m[4 * idx + 0];
        result.y = this.m[4 * idx + 1];
        result.z = this.m[4 * idx + 2];
        return result;
    };
    Matrix4.prototype.getColumnXY = function (idx, result) {
        if (result === void 0) {
            result = new Vec2();
        }
        result.x = this.m[4 * idx + 0];
        result.y = this.m[4 * idx + 1];
        return result;
    };
    Matrix4.prototype.transformPoint3 = function (v) {
        var m = this.m;
        var r = new Vec3(m[0] * v.x + m[4] * v.y + m[8] * v.z + m[12], m[1] * v.x + m[5] * v.y + m[9] * v.z + m[13], m[2] * v.x + m[6] * v.y + m[10] * v.z + m[14]);
        return r;
    };
    Matrix4.prototype.transformVec3 = function (v) {
        var m = this.m;
        return new Vec3(m[0] * v.x + m[4] * v.y + m[8] * v.z, m[1] * v.x + m[5] * v.y + m[9] * v.z, m[2] * v.x + m[6] * v.y + m[10] * v.z);
    };
    Matrix4.prototype.transformVec4 = function (v) {
        var m = this.m;
        return new Vec4(m[0] * v.x + m[4] * v.y + m[8] * v.z + m[12], m[1] * v.x + m[5] * v.y + m[9] * v.z + m[13], m[2] * v.x + m[6] * v.y + m[10] * v.z + m[14], m[3] * v.x + m[7] * v.y + m[11] * v.z + m[15]);
    };
    Matrix4.prototype.init = function (m) {
        if (m === void 0) {
            m = kIdentity4;
        }
        this.m.set(m);
    };
    return Matrix4;
}());
exports.Matrix4 = Matrix4;
var kIdentity4 = [1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1];
